import loadable from "@loadable/component"
import { Suspense } from "react"
import { Link } from "react-router-dom"

import { openModal } from "components/modal"
import Button from "components/ui/button"
import Picture from "components/ui/picture"
import {
  IS_AU,
  IS_CA,
  IS_UK,
  IS_US,
  IS_GE_CHECKOUT,
  jewlr_t,
  retry,
} from "helpers/application"
import {
  showHolidayShippingInfo,
  showServiceDisruptionNotice,
  holidayCTALink,
  isBeforeCutoffPeriod,
  isInCutoffPeriod,
} from "helpers/holiday"
import { getImgSrcSet } from "helpers/image"
import { cn } from "styles/utils"

const InstantGiftExplainedModal = loadable(
  () => retry(() => import("areas/instant-gift/explained/modal")),
  { ssr: false }
)

const deliveryData = IS_US()
  ? [
      {
        deliveryTime: (
          <>
            3 &ndash; 4 Business <br className="tablet:hidden block" />
            Days
          </>
        ),
        rate: <strong className="font-semibold">FREE</strong>,
        shippingService: "Standard Ground",
      },
      {
        deliveryTime: "2 Business Days",
        rate: (
          <>
            <strike>$19</strike> $12
          </>
        ),
        shippingService: "Expedited",
      },
      {
        deliveryTime: "1 Business Day",
        rate: (
          <>
            <strike>$25</strike> $17
          </>
        ),
        shippingService: "Express",
      },
    ]
  : IS_CA()
    ? [
        {
          deliveryTime: (
            <>
              2 &ndash; 5 Business <br className="tablet:hidden block" />
              Days
            </>
          ),
          rate: (
            <strong className="font-semibold">
              FREE with <br className="tablet:hidden block" /> every order
            </strong>
          ),
          shippingService: "Expedited Parcel",
        },
        {
          deliveryTime: (
            <>
              1 &ndash; 2 Business <br className="tablet:hidden block" />
              Days
            </>
          ),
          rate: (
            <>
              Postal code <br className="tablet:hidden block" /> dependant
            </>
          ),
          shippingService: "Express or Priority",
        },
      ]
    : IS_GE_CHECKOUT()
      ? [
          {
            deliveryTime: (
              <>
                7 &ndash; 10 Working <br className="tablet:hidden block" />
                Days
              </>
            ),
            rate: (
              <strong className="font-semibold">
                FREE with <br className="tablet:hidden block" /> every order
              </strong>
            ),
            shippingService: "Standard Delivery",
          },
          {
            deliveryTime: (
              <>
                5 &ndash; 8 Working <br className="tablet:hidden block" />
                Days
              </>
            ),
            rate: (
              <>
                Provided at <br className="tablet:hidden block" /> checkout
              </>
            ),
            shippingService: "Express Delivery",
          },
        ]
      : IS_AU()
        ? [
            {
              deliveryTime: (
                <>
                  5 &ndash; 7 Working <br className="tablet:hidden block" />
                  Days
                </>
              ),
              rate: (
                <strong className="font-semibold">
                  FREE with <br className="tablet:hidden block" /> every order
                </strong>
              ),
              shippingService: "Expedited Shipping",
            },
          ]
        : [
            {
              deliveryTime: (
                <>
                  4 &ndash; 5 Working <br className="tablet:hidden block" />
                  Days
                </>
              ),
              rate: (
                <strong className="font-semibold">
                  FREE with <br className="tablet:hidden block" /> every order
                </strong>
              ),
              shippingService: "Standard Delivery",
            },
            {
              deliveryTime: "3 Working Days",
              rate: (
                <>
                  Provided at <br className="tablet:hidden block" /> checkout
                </>
              ),
              shippingService: "Express Delivery",
            },
          ]

const ShippingInformation = () => {
  return (
    <>
      <div className="contained-wide widescreen:px-60 px-16">
        <h1 className="h1 mb-32">{jewlr_t("terms.Shipping")} Information</h1>
        <div className="flex flex-col">
          {showServiceDisruptionNotice() && (
            <div
              className={cn(
                "tablet:mb-60 tablet:p-40 border-l-5 border-primary mb-32 bg-[#5173ff1a] p-32",
                showHolidayShippingInfo() &&
                  "border-peridot tablet:mb-10 mb-8 bg-[#96b22c1a]"
              )}
            >
              <h3 className="h3 mb-8">Important Shipping Update</h3>
              <p className="body">
                We are utilizing our network of shipping partners to ensure you
                receive your order within 2&ndash;6 business days, minimizing
                any delays caused by the Canada Post {jewlr_t("terms.labor")}{" "}
                disruption. <strong>Note</strong>: We cannot ship to PO Boxes at
                this time; please provide a valid street address at checkout.
              </p>
            </div>
          )}
          {showHolidayShippingInfo() && (
            <div className="tablet:p-40 tablet:mb-10 border-l-5 border-peridot mb-8 bg-[#96b22c1a] p-32">
              <h3 className="h3 mb-8 capitalize">
                {isBeforeCutoffPeriod() || isInCutoffPeriod() ? (
                  <>
                    {isBeforeCutoffPeriod() ? "Shop Early" : "Order Today"} for{" "}
                    {sl.config.guaranteed_holiday} Delivery!
                  </>
                ) : (
                  `Shop Now for ${jewlr_t("terms.Personalized")} Gifts!`
                )}
              </h3>
              <p className="body">
                {isBeforeCutoffPeriod() || isInCutoffPeriod() ? (
                  <>
                    Shop now to avoid delays and receive your{" "}
                    {sl.config.guaranteed_holiday} gifts on time.{" "}
                    {isBeforeCutoffPeriod()
                      ? "All items are"
                      : "Many of our items are still"}{" "}
                    available for{" "}
                    {isBeforeCutoffPeriod()
                      ? `delivery for ${sl.config.guaranteed_holiday_alias}`
                      : `for ${sl.config.guaranteed_holiday_alias} delivery`}
                    . {jewlr_t("terms.Personalize")} your item and view the
                    available delivery dates for your item and location.
                    <div>
                      <Button
                        asChild
                        className="mt-16 font-semibold"
                        data-lc="shipping-information"
                        text
                        underline
                      >
                        <Link to={holidayCTALink()}>Shop Now</Link>
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    Order today &ndash; it&apos;s never too late! We start
                    handcrafting your {jewlr_t("terms.jewelry")} right away
                    {sl.config.enable_gift_preview_video ? (
                      <>
                        , and with our{" "}
                        <Button
                          asChild
                          data-lc="shipping-information"
                          underline
                        >
                          <Link to="/info/gift-preview-video">
                            Gift Preview Video
                          </Link>
                        </Button>{" "}
                        video, you can send a preview of your gift to a loved
                        one
                      </>
                    ) : null}
                    . Your actual gift will arrive soon after{" "}
                    {sl.config.guaranteed_holiday_alias}.
                  </>
                )}
              </p>
            </div>
          )}
          <div className="tablet:order-1">
            <h2 className="h2 tablet:mb-32 mb-16">Delivery Times & Rates</h2>
            <div className="tablet:mb-60 tablet:table mb-32 block w-full table-fixed border-collapse border-spacing-0 text-left">
              <div className="tablet:table-header-group hidden">
                <div className="border-grey-30 tablet:table-row w-full border-b">
                  <h3 className="h3 table-cell p-20 uppercase">
                    {jewlr_t("terms.Shipping")} Service
                  </h3>
                  <h3 className="h3 table-cell p-20 uppercase">
                    {jewlr_t("terms.Shipping")} Time
                  </h3>
                  <h3 className="h3 table-cell p-20 uppercase">Rates</h3>
                </div>
              </div>

              <div className="tablet:table-row-group block">
                {deliveryData.map((data, i) => (
                  <div
                    className="tablet:table-row border-grey-30 block w-full border-b"
                    key={`row-${i}`}
                  >
                    <p className="body tablet:table-cell tablet:p-20 tablet:font-normal tablet:normal-case tablet:w-auto block w-full px-20 pt-20 align-top font-semibold uppercase">
                      {data.shippingService}
                    </p>
                    <p className="body tablet:table-cell tablet:p-20 inline-block w-1/2 px-20 pb-20 pt-12 align-top">
                      {data.deliveryTime}
                    </p>
                    <p className="body tablet:table-cell tablet:p-20 inline-block w-1/2 px-20 pb-20 pt-12 align-top">
                      {data.rate}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="tablet:flex-row tablet:mb-60 mb-16 flex flex-col">
            <div className="tablet:basis-1/2 tablet:mb-0 tablet:mr-40 mb-40 basis-full">
              <h2 className="tablet:mb-20 h2 mb-10">
                Estimated Delivery &<br /> Manufacturing Time
              </h2>
              <p className="body">
                At Jewlr, each item is made-to-order and takes between 1 and 4
                days to manufacture. We calculate the estimated delivery dates
                on the item page using average manufacturing times and available{" "}
                {jewlr_t("terms.shipping")} methods. Your order confirmation
                email will also include the estimated{" "}
                {jewlr_t("terms.shipping")} date.
              </p>
            </div>
            <div className="tablet:basis-1/2 basis-full">
              {IS_US() && (
                <>
                  <h2 className="tablet:mb-20 h2 mb-10">
                    FREE {jewlr_t("terms.Shipping").toUpperCase()}
                    <br className="tablet:block hidden" /> to all 50 States
                  </h2>
                  <p className="body mb-20">
                    We offer free standard {jewlr_t("terms.shipping")} on all
                    items to all states &ndash; including Alaska and Hawaii. For
                    faster {jewlr_t("terms.shipping")} service, upgrade to an
                    Express {jewlr_t("terms.Shipping")} option.
                  </p>
                  <p className="body">
                    <b className="font-medium">Note:</b> Delivery times do{" "}
                    <b className="font-medium">not</b> include manufacturing
                    time. We {jewlr_t("terms.ship")} using UPS, FedEx, and USPS.
                    Please allow additional delivery time if{" "}
                    {jewlr_t("terms.shipping")} to APOs, FPOs, PO boxes or
                    remote locations.
                  </p>
                </>
              )}
              {IS_CA() && (
                <>
                  <h2 className="tablet:mb-20 h2 mb-10">
                    FREE {jewlr_t("terms.Shipping").toUpperCase()}
                    <br className="tablet:block hidden" /> within Canada
                  </h2>
                  <p className="body mb-20">
                    We offer free standard {jewlr_t("terms.shipping")} on all
                    items. For faster {jewlr_t("terms.shipping")} service,
                    upgrade to an Express {jewlr_t("terms.Shipping")} option.
                  </p>
                  <p className="body">
                    Delivery times do not include the manufacturing time. We{" "}
                    {jewlr_t("terms.ship")} using Canada Post, UPS, and FedEx.
                    Please allow additional time when{" "}
                    {jewlr_t("terms.shipping")} to PO Boxes or remote locations.
                  </p>
                </>
              )}
              {IS_UK() && (
                <>
                  <h2 className="tablet:mb-20 h2 mb-10">
                    FREE {jewlr_t("terms.Shipping").toUpperCase()}
                    <br className="tablet:block hidden" /> to the UK
                  </h2>
                  <p className="body mb-20">
                    We are pleased to offer free standard delivery on all items
                    to the UK. We handcraft your {jewlr_t("terms.Jewelry")} in
                    our workshop in Canada. Once complete, your order{" "}
                    {jewlr_t("terms.ship")}s via expedited courier service to
                    the United Kingdom.
                  </p>
                  <p className="body">
                    Delivery times do not include the manufacturing time.
                    Occasionally, we encounter delays that affect our quoted
                    delivery dates, such as customs clearance, weather, or
                    inaccurate addresses. These delays are beyond our control.
                  </p>
                </>
              )}
              {IS_AU() && (
                <>
                  <h2 className="tablet:mb-20 h2 mb-10">
                    FREE {jewlr_t("terms.Shipping").toUpperCase()}
                    <br className="tablet:block hidden" /> to Australia
                  </h2>
                  <p className="body mb-20">
                    We are pleased to offer free standard{" "}
                    {jewlr_t("terms.shipping")} on all items to Australia. We
                    handcraft your {jewlr_t("terms.Jewelry")} in our workshop in
                    Canada. Once complete, your order is shipped to our
                    Australian office, and from there, the Australia Post
                    Express Post Service will deliver it to you.
                  </p>
                  <p className="body">
                    {jewlr_t("terms.Shipping")} times do not include the
                    manufacturing time. Occasionally we encounter delays that
                    affect our quoted {jewlr_t("terms.shipping")} dates, such as
                    customs clearance, weather, or inaccurate addresses. These
                    delays are beyond our control.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
        {sl.config.enable_gift_preview_video && (
          <div className="tablet:flex-row tablet:py-32 flex flex-col py-16">
            <div className="tablet:basis-1/2 tablet:mb-0 tablet:mr-16 desktop:mr-40 mb-16 basis-full">
              <Picture
                alt="Gift Preview Video"
                className="w-full"
                fallback="https://cms.jewlr.com/instant_gift_2x_b53f0f6292/instant_gift_2x_b53f0f6292.jpg"
                srcSet={[
                  {
                    height: 600,
                    media: "(max-width: 767px)",
                    sizes: "100vw",
                    srcSet: getImgSrcSet(
                      `${sl.config.jewlr_imgsmbly_url}cms/m_instant_gift_2x_3bd7a92b17/m_instant_gift_2x_3bd7a92b17.jpg`,
                      686
                    ),
                    width: 686,
                  },
                  {
                    height: 1000,
                    media: "(min-width: 768px)",
                    sizes: "50vw",
                    srcSet: getImgSrcSet(
                      `${sl.config.jewlr_imgsmbly_url}cms/instant_gift_2x_b53f0f6292/instant_gift_2x_b53f0f6292.jpg`,
                      1280
                    ),
                    width: 1280,
                  },
                ]}
              />
            </div>
            <div className="tablet:basis-1/2 tablet:py-40 basis-full pb-24 pt-12">
              <h2 className="h2 mb-20">Gift Preview Video</h2>
              <h3 className="h3">Your Last-Minute Gifting Solution</h3>
              <p className="body mb-20">
                Create, order, and preview today &ndash; your gift ships fast!
              </p>
              <h3 className="h3">How it Works</h3>
              <p className="body mb-20">
                Create and order your custom {jewlr_t("jewelry")} today, and use
                Gift Preview Video to send a preview of your gift to a loved one
                via text or email. This video includes item details, delivery
                date, and your personal message.
              </p>
              <Button
                aria-label="Learn more about Gift Preview Video"
                className="font-semibold"
                onClick={() => openModal("#gift-preview-video-explained")}
                text
                underline
              >
                Learn More
              </Button>
            </div>
          </div>
        )}
      </div>
      {typeof window !== "undefined" && sl.config.enable_gift_preview_video && (
        <Suspense fallback={<div />}>
          <InstantGiftExplainedModal />
        </Suspense>
      )}
    </>
  )
}

ShippingInformation.propTypes = {}
export default ShippingInformation
